import React, { useState } from 'react';
import { apiService } from '../services/api';
import { Lock } from 'lucide-react';

interface LoginProps {
  onLoginSuccess: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiService.login(username, password);
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f0f4fb' }}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img
              src="/logo topbred 1.png"
              alt="TopBred"
              className="h-10 w-10 object-contain"
            />
            <span
              className="text-3xl tracking-wide pt-1.5"
              style={{ fontFamily: 'Azonix, sans-serif', color: '#132B64' }}
            >
              TOPBRED
            </span>
          </div>
          <p className="mt-2 text-center text-xs tracking-widest uppercase" style={{ color: '#233454', opacity: 0.7 }}>
            Horse Management Platform
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div style={{ borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div>
              <label htmlFor="username" className="sr-only">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="block w-full text-sm focus:outline-none"
                style={{ backgroundColor: '#ebf6e4', color: '#233454', padding: '14px 16px', border: 'none', borderBottom: '1px solid #cbe0be' }}
                onFocus={(e) => { e.target.style.boxShadow = 'inset 0 0 0 2px #132B64'; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                placeholder="Username"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full text-sm focus:outline-none"
                style={{ backgroundColor: '#ebf6e4', color: '#233454', padding: '14px 16px', border: 'none' }}
                onFocus={(e) => { e.target.style.boxShadow = 'inset 0 0 0 2px #132B64'; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                placeholder="Password"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Login Error
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-full text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: '#132B64', '--tw-ring-color': '#132B64' } as React.CSSProperties}
              onMouseEnter={(e) => { if (!loading) (e.currentTarget).style.backgroundColor = '#0f2350'; }}
              onMouseLeave={(e) => { if (!loading) (e.currentTarget).style.backgroundColor = '#132B64'; }}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" style={{ color: '#e6ecfa' }} />
                  Sign in
                </>
              )}
            </button>
          </div>
        </form>
        <p className="text-center text-sm" style={{ color: '#52514e' }}>
          Not using Topbred yet?{' '}
          <a href="/analysis" className="font-medium underline underline-offset-2" style={{ color: '#132B64' }}>See our video analysis</a>
        </p>
      </div>
    </div>
  );
}; 